import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteSignatures, UserKeys } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { SignatureModel } from '../../../../types';
import Typography from '@mui/material/Typography';

interface DeleteSignatureTemplateDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: number[];
  setSelectedRows: Dispatch<SetStateAction<number[]>>;
  changeLogRoles: string[];
  signatures: SignatureModel[];
  onDeleteSuccess?: () => void;
}

export default function DeleteSignatureTemplateDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
  changeLogRoles,
  signatures,
  onDeleteSuccess,
}: DeleteSignatureTemplateDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();

  const selectedSignatures = signatures.filter((item) =>
    selectedRows.includes(item.id)
  );

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteSignatures(selectedRows, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedRows.length === 1
            ? 'The signature template has been deleted.'
            : 'The signature templates have been deleted.',
      });

      queryClient.setQueryData(
        [UserKeys.Signatures],
        (oldData: SignatureModel[]) =>
          oldData.filter((sig) => !selectedRows.includes(sig.id))
      );

      setSelectedRows([]);
      setChangeLogMessage('');
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedRows.length === 1
            ? 'There was an issue deleting the selected template. Please refresh and try again.'
            : 'There was an issue deleting the selected templates. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.automation.signature_templates.delete_signature_template,
        {
          module: ModuleList.modules.automation.name,
          section: ModuleList.modules.automation.sections.signatureTemplates,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, ToolsRoles.DeleteTemplates.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const isBulk = selectedRows.length > 1;
  const templateName =
    selectedSignatures.length > 0 ? selectedSignatures[0].templateName : '';

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={
        isBulk ? 'Delete Signature Templates' : 'Delete Signature Template'
      }
      dialogContent={
        <>
          <Typography>
            {isBulk ? (
              <>
                Delete{' '}
                <strong>{selectedRows.length} signature templates</strong>?
              </>
            ) : (
              <>
                Delete <strong>{templateName}</strong>?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: isBulk ? 'Delete Templates' : 'Delete Template',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
